import axios from 'axios'
import actions from 'redux/actions'
import { constants, links, sentry } from 'common/helpers'

import type { AxiosPromise, AxiosError } from 'axios'
import type { RequestToken } from 'common/helpers/constants/requestTokens'

import { createRequest, mockRequest, DummyPromise, logToConsole, token } from './util'


// Set for active request states
const activeOnceRequests = new Map()
const activeRequestAborts = new Map()

const defaultOptions = {
  fixQuery: true,
  sameOrigin: true,
  multiple: false,
  abortPrevious: false,
  withErrorModal: true,
  /**
   * it will check nested errors in graphql mutations and queries
   * e.g. createReview.errors
   * it will show modal (if withErrorModal is true) and reject with errors
   */
  withNestedErrors: false,
  onComplete: () => {},
}

const defaultServerError = 'We`re having technical issues at that moment. Please try again later'

const createResponseHandler = (req: AxiosPromise, opts): Promise<any> => {
  const debug = `${opts.method.toUpperCase()} ${opts.endpoint}`
  const { isGraphQL } = opts

  let serverError
  let errStatusCode

  if (typeof req.finally !== 'function') {
    sentry.captureException('Request finally is not a function', {
      extra: {
        req,
        opts,
      },
    })

    return Promise.reject(new Error('No finally function'))
  }

  return req
    .finally(() => {
      activeOnceRequests.delete(opts.cacheKey)
      activeRequestAborts.delete(opts.endpoint)
    })
    .then(
      (response) => {
        const { data, status } = response

        token.set(opts, data, status)

        if ((data === null || data === undefined) && (typeof opts.modifyResult === 'function' || isGraphQL)) {
          sentry.captureException(`Body is null or undefined with modifyResult`, {
            extra: {
              method: opts.method.toUpperCase(),
              endpoint: opts.endpoint,
              body: opts.body,
              response,
              withModifyResult: typeof opts.modifyResult === 'function',
            },
          })

          serverError = defaultServerError
          errStatusCode = status

          return Promise.reject({
            res: response,
            statusCode: status,
            message: serverError,
            resData: {},
          })
        }

        if (isGraphQL) {
          let { errors, data: resData } = data

          // check nested errors, should be before modifyResult
          if (opts.withNestedErrors) {
            const [ nestedData ]: Array<any> = Object.values(resData)

            if (nestedData && nestedData.errors) {
              errors = nestedData.errors // eslint-disable-line
            }
          }

          if (typeof opts.modifyResult === 'function') {
            resData = opts.modifyResult(resData)
          }

          if (errors && errors.length) {
            if (opts.withErrorModal || opts.withHumbleErrorModal) {
              const [ error ] = errors

              actions.models.modals.open(constants.modals.ServerError, {
                content: error.message,
                // sentry data
                body: opts.body,
                query: opts.query,
                errors,
                endpoint: opts.endpoint,
                statusCode: status,
              })
            }

            return Promise.reject({ errors, data: data.data, resData })
          }

          return resData
        }

        // rest
        return typeof opts.modifyResult === 'function' ? opts.modifyResult(data) : data
      },
      (axiosError: AxiosError) => {
        // abortPrevious
        if (axios.isCancel(axiosError)) {
          return new DummyPromise()
        }

        const { response, request, message } = axiosError

        console.error(`Response err for ${opts.endpoint}:`, axiosError)

        if (response) {
          const { data, status } = response

          token.set(opts, data, status)

          if (status === 401 && !/register\/log-in/g.test(window.location.pathname)) {
            const redirectLink = `${window.location.pathname}${encodeURIComponent(window.location.search)}`
            window.location.href = `${links.login}?redirect=${redirectLink}`

            return new DummyPromise()
          }

          if (status < 500 && data) {
            const { title, message } = data

            if (opts.withHumbleErrorModal) {
              actions.models.modals.open(constants.modals.humble.ServerError, { title, text: message })
            }
            else if (opts.withErrorModal) {
              actions.models.modals.open(constants.modals.ServerError, {
                title,
                content: message,
                body: opts.body,
                query: opts.query,
                endpoint: opts.endpoint,
                statusCode: status,
                message,
              })
            }
          }
          else {
            serverError = defaultServerError
            errStatusCode = status
          }

          const { title, error, message, ...resData } = data || {}

          return Promise.reject({
            err: axiosError,
            error,
            message,
            res: { ...response, body: data },
            statusCode: status,
            resData,
          })
        }

        // no response
        serverError = `Connection failed: ${debug}`

        if (!request) {
          sentry.captureException('Axios fail to set up request', {
            extra: {
              err: axiosError,
              message: axiosError.message,
              method: opts.method.toUpperCase(),
              endpoint: opts.endpoint,
              body: opts.body,
              withModifyResult: typeof opts.modifyResult === 'function',
            },
          })
        }

        return Promise.reject({
          err: axiosError,
          res: response,
          message,
          resData: {},
        })
      }
    )
    .finally(() => {
      if (typeof opts.onComplete === 'function') {
        opts.onComplete()
      }

      // TODO remove withHumbleErrorModal and leave only 1 modal - added on 27.05.2020 by sonatskiy
      if (serverError) {
        if (opts.withHumbleErrorModal) {
          actions.models.modals.open(constants.modals.humble.ServerError)
        }
        else if (opts.withErrorModal) {
          actions.models.modals.open(constants.modals.SomethingWentWrong, {
            body: opts.body,
            query: opts.query,
            endpoint: opts.endpoint,
            serverError,
            statusCode: errStatusCode,
          })
        }
      }
    })
}

/**
 *
 * @param {Object} options
 * @param {String} options.endpoint
 * @param {String} options.method
 * @param {Object} options.headers
 * @param {Object} options.query
 * @param {Object} options.body
 * @param {String} options.tokenName
 * @param {Boolean} options.withCredentials
 * @param {Boolean} options.multiple - if request can be called multiple times
 * @param {String} options.mockError
 * @param {Object} options.mockResponse
 * @param {number} options.mockDelay
 *
 * @returns {Promise<any>}
 */
const sendRequest = (options): Promise<any> => {
  const opts = { ...defaultOptions, ...options }

  opts.isGraphQL = /graphql/.test(opts.endpoint)

  if (opts.isGraphQL) {
    opts.multiple = true
  }

  if (window.debug) {
    logToConsole(opts)
  }

  // in case of abortPrevious we don't need a cache
  if (!opts.multiple && !opts.abortPrevious) {
    opts.cacheKey = `${opts.method}:${opts.endpoint}:${opts.query ? JSON.stringify(opts.query) : ''}:${opts.body ? JSON.stringify(opts.body) : ''}`

    if (activeOnceRequests.has(opts.cacheKey)) {
      // return empty methods to prevent errors
      return activeOnceRequests.get(opts.cacheKey)
    }
  }

  if (typeof opts.onStart === 'function') {
    opts.onStart()
  }

  if (opts.abortPrevious) {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    const prevReqCancel = activeRequestAborts.get(opts.endpoint)

    opts.cancelToken = source.token

    if (prevReqCancel) {
      prevReqCancel()
    }

    activeRequestAborts.set(opts.endpoint, source.cancel.bind(source))
  }

  let req

  if (opts.mockError || opts.mockResponse || opts.mockDelay) {
    req = mockRequest(opts)
  }
  else {
    req = createRequest(opts)
  }

  const responseHandler = createResponseHandler(req, opts)

  if (!opts.multiple && !opts.abortPrevious) {
    activeOnceRequests.set(opts.cacheKey, responseHandler)
  }

  return responseHandler
}


type MockOpts = {
  mockDelay?: number
  mockError?: any
  mockResponse?: {
    data?: { [key: string]: any }
    status?: number
  }
}

type BaseOpts<Payload, ModifiedResult> = {
  query?: { [key: string]: any }
  fixQuery?: boolean
  multiple?: boolean
  sameOrigin?: boolean
  abortPrevious?: boolean
  withErrorModal?: boolean
  onStart?: () => any
  onComplete?: () => any
  modifyResult?: ModifiedResult extends undefined ? any : (data: Payload) => ModifiedResult
} & MockOpts

type PostOpts<Payload, ModifiedResult> = BaseOpts<Payload, ModifiedResult> & {
  body?: { [key: string]: any }
  data?: { [key: string]: any }
  formData?: boolean
  tokenName?: RequestToken
}

type GraphqlOpts<GQLVariables, GQLPayload, ModifiedResult> = BaseOpts<GQLPayload, ModifiedResult> & {
  body: {
    query: string
    variables?: GQLVariables
  }
  withNestedErrors?: boolean
}

type RequestResult<Payload, ModifiedResult> = Promise<ModifiedResult extends undefined ? Payload : ModifiedResult>

const graphql = <GQLVariables, GQLPayload, ModifiedResult = undefined>
  (name: string, opts: GraphqlOpts<GQLVariables, GQLPayload, ModifiedResult>): RequestResult<GQLPayload, ModifiedResult> =>
    sendRequest({ ...opts, endpoint: `graphql?name=${name}`, method: 'post' })

const getReq = <Payload = any, ModifiedResult = undefined>
  (endpoint: string, opts?: BaseOpts<Payload, ModifiedResult>): RequestResult<Payload, ModifiedResult> =>
    sendRequest({ ...(opts || {}), endpoint, method: 'get' })

const postReqByMethod = (method: string) =>
  <Payload = any, ModifiedResult = undefined>(endpoint: string, opts?: PostOpts<Payload, ModifiedResult>): RequestResult<Payload, ModifiedResult> =>
    sendRequest({ ...(opts || {}), endpoint, method })


export default {
  gql: graphql,
  get: getReq,
  post: postReqByMethod('post'),
  patch: postReqByMethod('patch'),
  put: postReqByMethod('put'),
  delete: postReqByMethod('delete'),
}
